import {
  action,
  computed,
  isObservable,
  makeObservable,
  observable,
} from 'mobx'

import type {
  FacetModel,
  ListAction,
  ListModel,
  ListNode,
  ListQuery,
  ListSchema,
  SelectionModel,
} from '~/client/src/shared/lists/core'
import {
  createListPipeline,
  initialQuery,
  listQueryReducer,
} from '~/client/src/shared/lists/core'
import { withDefaultOptionLabels } from '~/client/src/shared/lists/withDefaultOptionLabels'

import type { ListApi } from './ListApi'

export class ListStore<T> implements ListApi<T> {
  @observable.ref private queryState: ListQuery
  private readonly pipeline: ReturnType<typeof createListPipeline<T>> =
    createListPipeline<T>()
  private readonly initialQueryOverrides: Partial<ListQuery> | undefined
  public readonly schema: ListSchema<T>
  private readonly getAllRecords: (() => readonly T[]) | undefined

  public constructor(
    schema: ListSchema<T>,
    private readonly getRecords: () => readonly T[],
    opts?: {
      readonly initialQuery?: Partial<ListQuery>
      readonly getAllRecords?: () => readonly T[]
    },
  ) {
    this.schema = withDefaultOptionLabels(schema)
    this.initialQueryOverrides = opts?.initialQuery
    this.queryState = initialQuery(this.schema, this.initialQueryOverrides)
    this.getAllRecords = opts?.getAllRecords

    makeObservable(this)
  }

  public get query(): ListQuery {
    return this.queryState
  }

  public get inputRecords(): readonly T[] {
    return this.getRecords()
  }

  @computed
  public get model(): ListModel<T> {
    const records = snapshotObservableInput(this.inputRecords)
    const allRecords = this.getAllRecords
      ? snapshotObservableInput(this.getAllRecords())
      : undefined

    return this.pipeline(records, this.schema, this.queryState, allRecords)
  }

  @computed
  public get nodes(): readonly ListNode<T>[] {
    return this.model.nodes
  }

  @computed
  public get filteredRecords(): readonly T[] {
    return this.model.filteredRecords
  }

  @computed
  public get filteredCount(): number {
    return this.model.filteredCount
  }

  @computed
  public get facets(): readonly FacetModel[] {
    return this.model.facets
  }

  @computed
  public get sourceMapByFacet(): ListModel<T>['sourceMapByFacet'] {
    return this.model.sourceMapByFacet
  }

  @computed
  public get bandIds(): readonly string[] {
    return this.model.bandIds
  }

  @computed
  public get isAllCollapsed(): boolean {
    return this.model.isAllCollapsed
  }

  @computed
  public get selection(): SelectionModel<T> {
    return this.model.selection
  }

  @computed
  public get bandSelection(): ListModel<T>['bandSelection'] {
    return this.model.bandSelection
  }

  @action
  public dispatch(action: ListAction): void {
    this.queryState = listQueryReducer(this.queryState, action)
  }

  @action
  public reset(
    overrides: Partial<ListQuery> | undefined = this.initialQueryOverrides,
  ): void {
    this.queryState = initialQuery(this.schema, overrides)
  }
}

function snapshotObservableInput<T>(records: readonly T[]): readonly T[] {
  return records.some(record => isObservable(record))
    ? records.slice()
    : records
}
